/* ==========================================================================
   Sollero Fashion — Secondary Shared Scripts
   File: arScripts2.js

   This secondary bundle remains isolated from arScripts.js. The AR Story
   image + text and AR Features image + text sections are CSS-driven. AR
   Promise Cards uses this file only for the mobile in-view card state and
   Shopify Theme Editor lifecycle handling.
   ========================================================================== */

(() => {
  "use strict";

  /* Several sections may include this same deferred asset. Guard the module so
     duplicate script tags cannot register duplicate global listeners. */
  if (window.__hzPromiseCardsModuleLoaded) return;
  window.__hzPromiseCardsModuleLoaded = true;

  const hzPromiseCardsControllers = new WeakMap();
  const hzPromiseCardsMobileQuery = window.matchMedia("(max-width: 767px)");
  const hzPromiseCardsReducedMotionQuery = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  function hzPromiseCardsCreate(root) {
    if (!root || hzPromiseCardsControllers.has(root)) return;

    const cards = Array.from(
      root.querySelectorAll("[data-hz-promise-card]")
    ).filter((card) => card.closest("[data-hz-promise-cards]") === root);

    if (cards.length === 0) return;

    let frameId = null;
    let rootObserver = null;
    let rootIsVisible = true;
    let destroyed = false;

    function clearMobileState() {
      cards.forEach((card) => {
        card.classList.remove("hz__promiseCardsCard--mobileActive");
      });
    }

    function setMobileActiveCard(activeCard) {
      cards.forEach((card) => {
        card.classList.toggle(
          "hz__promiseCardsCard--mobileActive",
          card === activeCard
        );
      });
    }

    function updateMobileActiveCard() {
      frameId = null;

      if (destroyed) return;

      if (!hzPromiseCardsMobileQuery.matches || !rootIsVisible) {
        clearMobileState();
        return;
      }

      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
      const viewportCenter = viewportHeight / 2;
      const rootRect = root.getBoundingClientRect();
      const visibleCards = [];
      let closestCard = null;
      let closestDistance = Number.POSITIVE_INFINITY;
      let greatestVisibleHeight = 0;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const visibleTop = Math.max(0, rect.top);
        const visibleBottom = Math.min(viewportHeight, rect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight <= 0 || rect.height <= 0) return;

        visibleCards.push(card);

        const cardCenter = rect.top + rect.height / 2;
        const distance = Math.abs(cardCenter - viewportCenter);

        if (
          distance < closestDistance - 0.5 ||
          (Math.abs(distance - closestDistance) <= 0.5 &&
            visibleHeight > greatestVisibleHeight)
        ) {
          closestCard = card;
          closestDistance = distance;
          greatestVisibleHeight = visibleHeight;
        }
      });

      /* The first and last cards cannot always reach the viewport center when
         the section starts or ends near a page boundary. Pin those boundary
         states so every card can receive the mobile emphasis while scrolling. */
      if (visibleCards.length > 0 && rootRect.top >= -1) {
        closestCard = visibleCards[0];
      } else if (visibleCards.length > 0 && rootRect.bottom <= viewportHeight + 1) {
        closestCard = visibleCards[visibleCards.length - 1];
      }

      setMobileActiveCard(closestCard);
    }

    function scheduleMobileActiveUpdate() {
      if (destroyed || frameId !== null) return;

      frameId = window.requestAnimationFrame(updateMobileActiveCard);
    }

    function handleScroll() {
      if (!rootIsVisible || !hzPromiseCardsMobileQuery.matches) return;
      scheduleMobileActiveUpdate();
    }

    function handleViewportChange() {
      scheduleMobileActiveUpdate();
    }

    function handleMobileQueryChange() {
      if (!hzPromiseCardsMobileQuery.matches) {
        clearMobileState();
      }

      scheduleMobileActiveUpdate();
    }

    function selectBlock(blockId) {
      const selectedCard = cards.find(
        (card) => card.dataset.hzPromiseBlockId === blockId
      );

      if (!selectedCard) return;

      cards.forEach((card) => {
        card.classList.toggle(
          "hz__promiseCardsCard--editorActive",
          card === selectedCard
        );
      });

      if (hzPromiseCardsMobileQuery.matches) {
        selectedCard.scrollIntoView({
          behavior: hzPromiseCardsReducedMotionQuery.matches ? "auto" : "smooth",
          block: "center",
          inline: "nearest"
        });
      }

      scheduleMobileActiveUpdate();
    }

    function deselectBlock(blockId) {
      cards.forEach((card) => {
        if (!blockId || card.dataset.hzPromiseBlockId === blockId) {
          card.classList.remove("hz__promiseCardsCard--editorActive");
        }
      });

      scheduleMobileActiveUpdate();
    }

    function destroy() {
      destroyed = true;

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }

      rootObserver?.disconnect();
      rootObserver = null;

      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);
      hzPromiseCardsMobileQuery.removeEventListener(
        "change",
        handleMobileQueryChange
      );

      cards.forEach((card) => {
        card.classList.remove(
          "hz__promiseCardsCard--mobileActive",
          "hz__promiseCardsCard--editorActive"
        );
      });

      hzPromiseCardsControllers.delete(root);
    }

    if ("IntersectionObserver" in window) {
      rootObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          rootIsVisible = Boolean(entry?.isIntersecting);
          scheduleMobileActiveUpdate();
        },
        { threshold: 0 }
      );

      rootObserver.observe(root);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleViewportChange, { passive: true });
    window.visualViewport?.addEventListener("resize", handleViewportChange, {
      passive: true
    });
    window.visualViewport?.addEventListener("scroll", handleViewportChange, {
      passive: true
    });
    hzPromiseCardsMobileQuery.addEventListener(
      "change",
      handleMobileQueryChange
    );

    hzPromiseCardsControllers.set(root, {
      destroy,
      selectBlock,
      deselectBlock
    });

    scheduleMobileActiveUpdate();
  }

  function hzPromiseCardsInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-promise-cards]")) {
      hzPromiseCardsCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-promise-cards]")
      .forEach(hzPromiseCardsCreate);
  }

  function hzPromiseCardsDestroyWithin(scope) {
    const roots = [];

    if (scope.matches?.("[data-hz-promise-cards]")) {
      roots.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-promise-cards]")
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzPromiseCardsControllers.get(root)?.destroy();
    });
  }

  function hzPromiseCardsHandleBlockSelect(event) {
    const card = event.target.closest?.("[data-hz-promise-card]");
    const root = card?.closest?.("[data-hz-promise-cards]");

    if (!card || !root) return;

    hzPromiseCardsControllers
      .get(root)
      ?.selectBlock(card.dataset.hzPromiseBlockId);
  }

  function hzPromiseCardsHandleBlockDeselect(event) {
    const card = event.target.closest?.("[data-hz-promise-card]");
    const root = card?.closest?.("[data-hz-promise-cards]");

    if (!card || !root) return;

    hzPromiseCardsControllers
      .get(root)
      ?.deselectBlock(card.dataset.hzPromiseBlockId);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzPromiseCardsInitWithin(document),
      { once: true }
    );
  } else {
    hzPromiseCardsInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    hzPromiseCardsInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzPromiseCardsDestroyWithin(event.target);
  });

  document.addEventListener(
    "shopify:block:select",
    hzPromiseCardsHandleBlockSelect
  );
  document.addEventListener(
    "shopify:block:deselect",
    hzPromiseCardsHandleBlockDeselect
  );
})();
